import { pushWithRegion } from 'actions/NavigationActions'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import useAgentHubAgentStatus from 'agentHub/hooks/useAgentHubAgentStatus'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import HeroImage from 'content/components/HeroImage'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import AgentHubAccountSetupModal from './AgentHubAccountSetupModal'

const CoveredBackground = styled(HeroImage)`
  flex-grow: 1;
  min-width: 100vw;
  min-height: 100vh;
`

function AgentHubCompleteAccountSetupPage() {
  const { token } = useQsQueryParams<{ token: string }>()
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const accountEmail = useAppSelector(state => state.auth.account.email)
  const agent = useAgentHubAgentStatus({ registrationToken: token })
  const agentEmail = agent.data?.email ?? accountEmail

  const accountSetupCompleted = agent.data?.status === 'onboarded'

  const isReady = Boolean(agent.data?.status) && Boolean(agentEmail)
  const tokenResponseError = agent.error

  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (tokenResponseError) {
      if (agent.data?.status === '400') {
        showSnackbar(intl.formatMessage({
          id: 'gWqQmPiVImSmoWNS1fQG',
          defaultMessage: 'Invalid token.',
          description: 'Snackbar error when agent hub registration token is invalid',
        }), 'critical')
      } else {
        showSnackbar(intl.formatMessage({
          id: '2mo_Le_s4pPUvzeG0nJq',
          defaultMessage: 'This link is either expired or already used, for assistance contact support by e-mail or phone.',
          description: 'Snackbar error when agent hub registration link is expired or already used',
        }), 'critical')
      }

      showModal<AccountAccessModalResult>(<AccountAccessModal
        dismissable={false}
        initialMode="login"
      />).then((result) => {
        if (result.loggedIn) {
          dispatch(pushWithRegion('/'))
        }
      })
    }

    if (isReady) {
      if (accountSetupCompleted) {
        // already setup, do a login instead
        showModal<AccountAccessModalResult>(<AccountAccessModal
          dismissable={false}
          initialMode="loginPassword"
          defaultEmail={agent.data?.email}
        />).then((result) => {
          if (result.loggedIn) {
            dispatch(pushWithRegion('/'))
          }
        })
      } else {
        // not setup, show the setup modal
        showModal(<AgentHubAccountSetupModal
          agentId={agent.data?.agentId!}
          agentEmail={agent.data?.email!}
        />).then(() => {
          dispatch(pushWithRegion(`/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`))
        })
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, accountSetupCompleted, tokenResponseError])

  return <CoveredBackground fit="center" id="agent-hub-account-setup-bg" />
}

export default AgentHubCompleteAccountSetupPage
